import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';
import { ComparedDateRangePicker } from '@bingads-webui-react/compared-date-range-picker';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  getToday,
  getMinimumSupportedDay,
  getCivilDateFromString,
  getMinimumSupportedWarning,
  getFilteredTimeRanges,
  isCivilDateGreaterThanEqual
} from './date-picker-utils';
import { getStyle } from './date-picker-style';

function DatePickerBase({
  reportSchema,
  dateRange,
  onChange, setWarning,
  timeZone,
  timeGrain,
  getFilteredRanges,
  getMinimumDay,
  getMinimumWarning,
}) {
  const classes = useDefaultStyles(getStyle);

  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);

  const dateFormat = putil.get(serverContext, 'CurrentUser.CultureDateTimeFormat.ShortDatePattern');

  const ianaTimeZone = toIANATimeZone(timeZone);

  const today = getToday(i18n, timeZone);
  const minSupportedDay = getMinimumDay(reportSchema, timeGrain, today);
  const specificRanges = getFilteredRanges(timeGrain, today, minSupportedDay, reportSchema, i18n);
  const selectedTimeRange = dateRange.key;
  let startDate;
  let endDate;
  let selectedRangeId = 4;

  const allowedTimeRanges = {};
  specificRanges.forEach((node) => { allowedTimeRanges[node.key] = node; });

  if (selectedTimeRange !== 'Custom') {
    startDate = allowedTimeRanges[selectedTimeRange].range.start;
    endDate = allowedTimeRanges[selectedTimeRange].range.end;
    selectedRangeId = allowedTimeRanges[selectedTimeRange].rangeId;
  } else {
    startDate = getCivilDateFromString(dateRange.start);
    endDate = getCivilDateFromString(dateRange.end);
  }

  const onPickDateRanges = (result) => {
    const { rangeType, selectedRange } = result;
    if (isCivilDateGreaterThanEqual(minSupportedDay, selectedRange.start)) {
      const warningMessage = i18n.getString(getMinimumWarning(reportSchema, timeGrain, today));
      setWarning(warningMessage);
    } else {
      setWarning(undefined);
    }
    if (rangeType.key === 'Custom') {
      onChange({
        key: rangeType.key,
        start: selectedRange.start.toString(),
        end: selectedRange.end.toString(),
      });
    } else {
      onChange({
        key: rangeType.key,
        start: undefined,
        end: undefined,
      });
    }
  };
  return (
    <div className={classes.reportDatePicker}>
      <ComparedDateRangePicker
        disableCompare
        displayType
        key={timeGrain + timeZone}
        i18n={i18n}
        rangeId={selectedRangeId}
        selectedRange={{ start: startDate, end: endDate }}
        specificRanges={specificRanges}
        timeZone={ianaTimeZone}
        onPickDateRanges={onPickDateRanges}
        minDate={minSupportedDay.plus({ days: -1 })}
        maxDate={today.plus({ days: 1 })}
        dateFormat={dateFormat}
      />
    </div>
  );
}

DatePickerBase.propTypes = {
  dateRange: PropTypes.shape({
    key: PropTypes.string.isRequired,
    start: PropTypes.string,
    end: PropTypes.string,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
  setWarning: PropTypes.func.isRequired,
  timeGrain: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  today: PropTypes.shape({
    day: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  i18n: PropTypes.shape({}).isRequired,
  reportSchema: PropTypes.shape({}).isRequired,
  getFilteredRanges: PropTypes.func,
  getMinimumDay: PropTypes.func,
  getMinimumWarning: PropTypes.func,
};

DatePickerBase.defaultProps = {
  getFilteredRanges: getFilteredTimeRanges,
  getMinimumDay: getMinimumSupportedDay,
  getMinimumWarning: getMinimumSupportedWarning,
};

export const DatePicker = DatePickerBase;
